import { type ReactNode, useEffect, useState } from 'react';
import styles from './Panel.module.css';

interface PanelProps {
  title: string;
  children: ReactNode;
  headerRight?: ReactNode;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export default function Panel({
  title,
  children,
  headerRight,
  collapsed: collapsedProp = false,
  onCollapsedChange,
}: PanelProps) {
  const [collapsed, setCollapsed] = useState(collapsedProp);

  useEffect(() => {
    setCollapsed(collapsedProp);
  }, [collapsedProp]);

  const handleToggle = () => {
    setCollapsed((c) => {
      const next = !c;
      onCollapsedChange?.(next);
      return next;
    });
  };

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>{title}</h2>
        {headerRight && <div className={styles.headerRight}>{headerRight}</div>}
        <button
          type="button"
          tabIndex={-1}
          className={styles.collapseButton}
          onClick={handleToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
        >
          <svg
            className={styles.chevron}
            data-collapsed={collapsed}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="4 6 8 10 12 6" />
          </svg>
        </button>
      </div>
      <div className={styles.panelBody} data-collapsed={collapsed}>
        <div>{children}</div>
      </div>
    </div>
  );
}
